import { Taxonomy } from "@common/categories";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import type { Ledger } from "@common/contracts/ledger.contract";
import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import {
    RuleActionType,
    RuleConditionType,
    RuleGroupType,
    serializeAccrualAccountActionValue,
    TransactionRuleActionDefinition,
    TransactionRuleConditionDefinition,
} from "@common/types/transactionRule";

export interface InheritedRuleTransactionContext {
    entityId: number;
    counterpartyId?: string | null;
    bankDescription?: string | null;
    categoryId?: string | null;
    categoryTaxonomy?: Taxonomy | null;
    isGlFirst?: boolean;
    previousClassIds?: string[];
    /** Properties a rule last set on the transaction, before the edit. */
    ruleAppliedActionTypes?: RuleActionType[];
    ledgers?: Array<Pick<Ledger, "id" | "basis" | "isPrimary">>;
}

export interface InheritedRuleSuggestion {
    definition: {
        conditions: TransactionRuleConditionDefinition[];
        actions: TransactionRuleActionDefinition[];
        applyToExistingTransactions: boolean;
        requireExplicitConditions: true;
        scopeEntityId: number;
    };
    groupType: RuleGroupType;
}

function action(
    actionType: RuleActionType,
    value: TransactionRuleActionDefinition["value"],
): TransactionRuleActionDefinition {
    return {
        actionType,
        value,
        transferDirection: null,
    };
}

function buildMatchConditions({
    payload,
    transaction,
    actions,
}: {
    payload: UpdateTransactionDto;
    transaction: InheritedRuleTransactionContext;
    actions: TransactionRuleActionDefinition[];
}): TransactionRuleConditionDefinition[] | undefined {
    const conditions: TransactionRuleConditionDefinition[] = [
        {
            conditionType: RuleConditionType.ENTITY,
            value: [transaction.entityId],
            transferDirection: null,
        },
    ];
    const counterpartyId =
        payload.counterpartyId !== undefined
            ? payload.counterpartyId
            : transaction.counterpartyId;
    if (counterpartyId) {
        conditions.push({
            conditionType: RuleConditionType.COUNTERPARTY,
            value: [counterpartyId],
            transferDirection: null,
        });
    }
    const isClassRule = actions.some(
        ({ actionType }) => actionType === RuleActionType.CLASS,
    );
    const isCategoryRule = actions.some(
        ({ actionType }) => actionType === RuleActionType.CATEGORY,
    );
    const { categoryId } = transaction;
    const isCategorized =
        transaction.categoryTaxonomy !== Taxonomy.uncategorized;
    const shouldMatchCategory =
        isClassRule &&
        !isCategoryRule &&
        !transaction.isGlFirst &&
        isCategorized &&
        categoryId;
    if (shouldMatchCategory) {
        conditions.push({
            conditionType: RuleConditionType.CATEGORY,
            value: [categoryId],
            transferDirection: null,
        });
    }
    const { bankDescription } = transaction;
    if (!counterpartyId && bankDescription) {
        // The matcher compares descriptions exactly while saved condition
        // values are trimmed, so padded text could never match its source.
        if (bankDescription !== bankDescription.trim()) {
            return undefined;
        }
        conditions.push({
            conditionType: RuleConditionType.DESCRIPTION_IS,
            value: [bankDescription],
            transferDirection: null,
        });
    }
    return conditions;
}

function resolveLedgerAccountId({
    payload,
    transaction,
    basis,
}: {
    payload: UpdateTransactionDto;
    transaction: InheritedRuleTransactionContext;
    basis: AccountingLedgerBasis;
}): string | undefined {
    const override = payload.accountOverrides?.find((entry) =>
        transaction.ledgers?.some(
            (ledger) =>
                ledger.id === entry.accountingLedgerId &&
                ledger.isPrimary &&
                ledger.basis === basis,
        ),
    );
    if (override) {
        return override.accountId ?? undefined;
    }

    const directAccountId =
        basis === AccountingLedgerBasis.cash
            ? payload.accountId
            : payload.accrualAccountId;
    return directAccountId ?? undefined;
}

export function buildInheritedRuleSuggestion({
    payload,
    transaction,
}: {
    payload: UpdateTransactionDto;
    transaction: InheritedRuleTransactionContext;
}): InheritedRuleSuggestion | undefined {
    const hasUnsupportedLedger = payload.accountOverrides?.some(
        (override) =>
            override.accountId &&
            !transaction.ledgers?.some(
                (ledger) =>
                    ledger.id === override.accountingLedgerId &&
                    ledger.isPrimary,
            ),
    );
    if (hasUnsupportedLedger) {
        return undefined;
    }
    const actions: TransactionRuleActionDefinition[] = [];
    if (payload.categoryId) {
        actions.push(action(RuleActionType.CATEGORY, payload.categoryId));
    }
    if (payload.entityId !== undefined) {
        actions.push(action(RuleActionType.ENTITY, payload.entityId));
    }
    if (payload.customerId) {
        actions.push(action(RuleActionType.CUSTOMER, payload.customerId));
    }
    if (payload.memo?.trim()) {
        actions.push(action(RuleActionType.MEMO, payload.memo));
    }
    const previousClassIds = transaction.previousClassIds;
    const addedClassIds = previousClassIds
        ? payload.classIds?.filter((id) => !previousClassIds.includes(id))
        : undefined;
    if (payload.classIds && addedClassIds?.length) {
        actions.push(action(RuleActionType.CLASS, payload.classIds));
    }
    const cashAccountId = resolveLedgerAccountId({
        payload,
        transaction,
        basis: AccountingLedgerBasis.cash,
    });
    const accrualAccountId = resolveLedgerAccountId({
        payload,
        transaction,
        basis: AccountingLedgerBasis.accruals,
    });
    if (cashAccountId) {
        actions.push(action(RuleActionType.ACCOUNT_ID, cashAccountId));
    }
    if (accrualAccountId) {
        actions.push(
            action(
                RuleActionType.ACCRUAL_ACCOUNT_ID,
                serializeAccrualAccountActionValue({
                    accountId: accrualAccountId,
                    autoConfirm: false,
                }),
            ),
        );
    }
    // A property last set by a rule is the rule's business, not a new
    // decision by the user, so it does not seed a suggestion.
    const userSetActions = actions.filter(
        ({ actionType }) =>
            !transaction.ruleAppliedActionTypes?.includes(actionType),
    );
    if (userSetActions.length === 0) {
        return undefined;
    }
    const conditions = buildMatchConditions({
        payload,
        transaction,
        actions: userSetActions,
    });
    if (!conditions) {
        return undefined;
    }
    return {
        groupType: userSetActions.every(
            ({ actionType }) =>
                actionType === RuleActionType.ACCOUNT_ID ||
                actionType === RuleActionType.ACCRUAL_ACCOUNT_ID,
        )
            ? RuleGroupType.ACCOUNTING
            : RuleGroupType.TRANSACTION,
        definition: {
            conditions,
            actions: userSetActions,
            applyToExistingTransactions: false,
            requireExplicitConditions: true,
            scopeEntityId: transaction.entityId,
        },
    };
}
